import React, { useEffect, useState } from "react";
import Media from "@qlik/qlik/src/components/Media";
import Cta from "@qlik/qlik/src/components/Cta";
import mapButtonType from "@qlik/qlik/src/components/Cta/mapButtonType";
import ContentSlider from "@qlik/qlik/src/components/ContentSlider";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import formatContentfulRichText from "@qlik/qlik/src/lib/format-rich-text-content";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import { renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";
import * as styles from "./styles.module.css";

// Slider settings
const settings = {
    dots: true,
    infinite: true,
    lazyLoad: false,
    arrows: false,
    speed: 1000,
    slidesToShow: 2,
    slidesToScroll: 2,
    // Responsive settings for different breakpoints
    responsive: [
        {
            breakpoint: 768,
            settings: {
                slidesToShow: 2,
            },
        },
        {
            breakpoint: 10000,
            settings: "unslick",
        },
    ],
};

/**
 * Full width section with Icons (Left and Right) component.
 * @param {Object} props - The component props.
 * @returns {JSX.Element} The rendered component.
 */
const FullWidthSectionIconsLeftRight = ({ data }) => {
    // State variable to check if slider is initialized
    const [sliderInitialized, setSliderInitialized] = useState(false);
    // Destructure the data object
    const { cardItems = [], cardCollections = [], additionalConfiguration, backgroundColor, customId } = data ?? {};
    // Get the padding class name based on the additional configuration
    const spacingClassNames = getSpacingClassName(additionalConfiguration, "");
    const { sectionColorMapping } = getColorClassName(backgroundColor);

    // Initialize slider on component mount
    useEffect(() => {
        setSliderInitialized(true);
    }, []);

    // Render the component
    return (
        <section className={`pt-15 pb-30 md:py-30 ${spacingClassNames} ${sectionColorMapping?.bgColor}`} id={customId}>
            <div className={`${styles.cardsWrapper} container`}>
                <div className="row">
                    <div className="md:col-4">
                        {cardItems?.map((cardItem) => (
                            <div key={cardItem.id}>
                                {cardItem?.eyebrowText && (
                                    <div className="tag-1 tracking-wide5 mb-5 md:mb-7.5">{cardItem.eyebrowText}</div>
                                )}
                                {cardItem?.title && (
                                    <h2
                                        className="title-2 mb-5 md:mb-7.25"
                                        // eslint-disable-next-line react/no-danger
                                        dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(cardItem.title) }}
                                    />
                                )}
                                {cardItems[0]?.body && (
                                    <div className="mb-10 md:pr-0.5">
                                        {renderRichText(cardItems[0]?.body, formatContentfulRichText())}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="md:col-8">
                        <div className={`xl:pl-17.5 ${!sliderInitialized ? "sliderNotInitialized" : ""}`}>
                            <ContentSlider
                                settings={settings}
                                className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 md:gap-y-19.5 md:gap-x-15 qlikSlider"
                            >
                                {cardCollections?.[0]?.cardItems?.map((card) => (
                                    <div key={card.id} className="h-full">
                                        <Cta
                                            data={card.cta?.[0]}
                                            className="pr-0 flex flex-col justify-between h-full group hover:no-underline after:hidden"
                                        >
                                            <div>
                                                {(card?.media?.[0].desktopMedia?.length ||
                                                    card?.media?.[0].mobileMedia?.length) > 0 && (
                                                    <Media
                                                        media={card?.media?.[0]}
                                                        lazyLoad={false}
                                                        className="mb-3.75 md:mb-7.5 object-cover w-9.5 h-9.5 md:w-12 md:h-12"
                                                    />
                                                )}
                                                {card?.title && (
                                                    <h3 className="mb-3.75 line-clamp-3 text-base tracking-normal xl:title-5">
                                                        {card?.title}
                                                    </h3>
                                                )}
                                                {card?.body && (
                                                    <div className={styles.CardRichText}>
                                                        {renderRichText(card?.body, formatContentfulRichText())}
                                                    </div>
                                                )}
                                            </div>
                                            {card.cta?.[0]?.text && (
                                                <p
                                                    className={`${mapButtonType(
                                                        card.cta?.[0]?.type
                                                    )} mt-6.25 md:mb-0 link-text xl:group-hover:after:translate-x-0.75 xl:group-hover:text-dark-green xl:group-hover:after:arrow-green-dark`}
                                                >
                                                    {card.cta?.[0]?.text}
                                                </p>
                                            )}
                                        </Cta>
                                    </div>
                                ))}
                            </ContentSlider>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FullWidthSectionIconsLeftRight;
